/**
 * Reads a layout breakpoint that is DEFINED IN SCSS.
 *
 * `src/variables.scss` is the single source for every breakpoint the layout and the TypeScript both
 * care about, and `styles.scss` re-exports each one as a `--breakpoint-*` custom property on
 * `:root`. This reads them back, so a number is changed in exactly one place (ADO #8909).
 *
 * It is that way round -- SCSS owning the values and TS deriving -- because a media query cannot use
 * a custom property: `@media (min-height: var(--x))` is not valid CSS. SCSS therefore has to hold
 * the literal, so anything else has to follow it.
 *
 * Previously main-image.component.ts kept its own copies (770 / 630 / 430 / 890) next to a comment
 * asking whoever came along to keep them in step with variables.scss. Nothing enforced it, and the
 * failure is invisible: the layout switches at one width while the annotations switch at another,
 * which reads as a rendering glitch in a narrow band rather than as two numbers disagreeing.
 */
export function readBreakpoint(customProperty: string, fallback: number): number {
  // SSR / unit tests without a document: fall back silently, there is no stylesheet to read.
  if (typeof document === 'undefined' || !document.documentElement) {
    return fallback;
  }

  const raw = getComputedStyle(document.documentElement).getPropertyValue(customProperty);
  const parsed = Number.parseFloat(raw);

  if (!raw || Number.isNaN(parsed)) {
    // Loudly, rather than quietly using the fallback: a missing custom property means styles.scss and
    // this call have drifted apart, and the fallback would hide exactly the drift this file exists to
    // prevent. The fallback keeps the app rendering; the error is what gets it fixed.
    console.error(
      `[breakpoint] ${customProperty} is not declared on :root -- falling back to ${fallback}. `
      + 'Check the :root block in styles.scss against src/variables.scss.'
    );
    return fallback;
  }

  return parsed;
}
